// Trabajo realizado / presupuesto de reparación de UN equipo, diseño limpio (18-oct-2026).
// Orden: cliente | equipo → lo que se hizo (si no entra como concepto) → detalle → total con IVA
// según el cliente → observaciones → fotos (lo más grandes que entren) → firmas.
import { cargarFoto } from '../helpers.js';
import { montosDescuento, construirFilasItem } from '../pdfShared.js';
import { ubicacionLegible } from '../porEquipo.js';
import { altoGaleriaFotos, dibujarGaleriaFotos, ALTOS_GALERIA } from '../bloques/fotosQR.js';
import { getEmpresa, M } from '../theme.js';
import { F, LIMITE, texto, rotulo, plata, datos, parrafo, tabla, totales, firmas, colCliente, espacio, nuevaHoja } from './base.js';

const LARGO_CONCEPTO = 60;
export const GARANTIA = 'Garantía 90 días sobre mano de obra · repuestos según fabricante';

// Filas del detalle de un equipo: [concepto, cant, unitario, importe]. La mano de obra lleva
// lo que se hizo como concepto cuando entra; si no, "Mano de obra" y el texto va aparte.
export function filasDeItem(item, { sinPrecios = false } = {}) {
    const trabajo = String(item.trabajo || item.trabajoRealizado || item.resumenTexto || '').replace(/\s+/g, ' ').trim();
    const crudas = construirFilasItem(item);
    let trabajoEnTabla = false;
    const filas = crudas.map((f, i) => {
        let concepto = f.concepto;
        if (i === 0 && f.esServicio) {
            if (trabajo && !item.esVisita && trabajo.length <= LARGO_CONCEPTO) { concepto = trabajo; trabajoEnTabla = true; }
            else concepto = item.esVisita ? 'Visita y diagnóstico' : 'Mano de obra';
        }
        const num = (s) => Number(String(s).replace(/\./g, '').replace(',', '.')) || 0;
        return sinPrecios ? [concepto, String(f.cant)] : [concepto, String(f.cant), plata(num(f.unitario)), plata(num(f.importe))];
    });
    return { filas, trabajo, trabajoEnTabla };
}

// Fotos del equipo con su rótulo (trabajos viejos: estado inicial / final)
export async function fotosDeItem(item, { esPresupuesto = false, extrasSueltas = [] } = {}) {
    const [a, d, ...ex] = await Promise.all([
        cargarFoto(item.fotoAntes || item.fotoEquipo || null), cargarFoto(item.fotoDespues || null),
        ...(item.fotosExtra || []).slice(0, 2).map(f => cargarFoto(f).catch(() => null)),
    ]);
    const rot = item.fotosRotulos;
    return [
        { foto: a, label: rot ? rot.antes : (esPresupuesto ? null : 'Estado inicial') },
        { foto: d, label: rot ? rot.despues : (esPresupuesto ? null : 'Estado final') },
        ...ex.map((f, i) => ({ foto: f, label: rot ? (rot.extra || [])[i] : null })),
        ...extrasSueltas.map(f => ({ foto: f, label: null })),
    ].filter(g => g.foto).slice(0, 8).map((g, i) => ({ ...g, label: String(g.label || `Foto ${i + 1}`).toUpperCase() }));
}

export function condicionesPresupuesto(esVisita) {
    const propias = (getEmpresa().condicionesPDF || '').trim();
    if (propias) return propias;
    return esVisita ? 'El valor de la visita se descuenta si se hace la reparación.' : 'Visita sin reparación: 50% de la mano de obra.';
}

export async function generarUnEquipoV2(doc, {
    ctx, item, cliente, sede, y, esPresupuesto = false,
    firmaCliente, firmaTecnico, aclaracionCliente = '', aclaracionTecnico = '',
    descuentoPorcentaje = 0, descuentoAlcance = 'TOTAL', totales: tot = null, garantiaTexto = null,
    proximoMantenimiento = null, leyenda = '', sinPrecios = false, fotosTrabajo = [],
    condicionIva = null, pctEfectivo = 0, cobradoEfectivo = false, montoCobrado = null,
}) {
    // Cliente | equipo
    const serie = item.equipoSerial && !['MOSTRADOR', 'SIN-SN', 'S/N'].includes(String(item.equipoSerial).toUpperCase()) ? item.equipoSerial : null;
    const ubic = ubicacionLegible(item);
    const modelo = item.modeloEquipo || item.equipoModelo || null;
    y = datos(doc, y, colCliente(cliente, sede),
        serie || modelo ? { rotulo: 'Equipo', nombre: serie ? `N/S ${serie}` : modelo, lineas: [serie ? modelo : null, ubic] } : null);

    // Detalle
    const { filas, trabajo, trabajoEnTabla } = filasDeItem(item, { sinPrecios });
    if (trabajo && !trabajoEnTabla) y = parrafo(doc, ctx, y, esPresupuesto ? 'Trabajo a realizar' : 'Trabajo realizado', trabajo);
    if (filas.length) {
        y = tabla(doc, ctx, y, sinPrecios
            ? [{ titulo: 'Concepto' }, { titulo: 'Cant.', ancho: 16, derecha: true }]
            : [{ titulo: 'Concepto' }, { titulo: 'Cant.', ancho: 14, derecha: true }, { titulo: 'Unitario', ancho: 30, derecha: true }, { titulo: 'Importe', ancho: 30, derecha: true }],
            filas);
    }

    // Total
    const garantia = esPresupuesto && item.esVisita ? null : (garantiaTexto || GARANTIA);
    const notas = [garantia, proximoMantenimiento ? `Próximo mantenimiento sugerido: ${proximoMantenimiento}` : null];
    if (sinPrecios) {
        y = parrafo(doc, ctx, y + 3, 'Aclaración', 'Documento informativo, sin precios.');
    } else if (!filas.length) {
        y = espacio(doc, ctx, y, 12);
        texto(doc, 'Precio a coordinar con el cliente', M, y + 5, { tam: F.nombre, negrita: true });
        y += 11;
    } else {
        const totalBruto = parseFloat(item.totalCalculado || item.costo || 0);
        const md = montosDescuento({ totalBruto, descuentoPorcentaje, descuentoAlcance, totales: tot });
        y = totales(doc, ctx, y, {
            subtotal: md.subtotal, descuentos: md.descuento > 0 ? [{ label: md.label, monto: md.descuento }] : [], total: md.total,
            condicionIva, esPresupuesto, pctEfectivo, cobradoEfectivo, montoCobrado, notas,
        });
    }
    if (esPresupuesto && !sinPrecios) y = parrafo(doc, ctx, y, 'Condiciones', condicionesPresupuesto(item.esVisita), { maxRenglones: 3 });
    if (String(leyenda || '').trim() && !/90 d.*mano de obra/i.test(leyenda)) y = parrafo(doc, ctx, y, 'Observaciones', leyenda, { maxRenglones: 4 });

    // Fotos: el tamaño más grande con el que entran ellas y las firmas en esta hoja
    const galeria = await fotosDeItem(item, { esPresupuesto, extrasSueltas: fotosTrabajo });
    const altoFirmas = firmaCliente || firmaTecnico ? 28 : 0;
    if (galeria.length) {
        const entra = ALTOS_GALERIA.find(a => y + 6 + altoGaleriaFotos(galeria, a) + altoFirmas <= LIMITE);
        const alto = entra || ALTOS_GALERIA[ALTOS_GALERIA.length - 1];
        if (y + 6 + alto + 9 > LIMITE) y = nuevaHoja(doc, ctx);
        rotulo(doc, esPresupuesto ? 'Fotos del equipo' : 'Registro fotográfico', M, y + 1);
        y = dibujarGaleriaFotos(doc, { y: y + 4, fotos: galeria, compacto: alto, titulo: null, alSaltar: () => nuevaHoja(doc, ctx) });
        y += 2;
    }

    // Firmas
    y = firmas(doc, ctx, y + 2, { firmaCliente, firmaTecnico, aclaracionCliente, aclaracionTecnico });
    return y;
}

